import { forwardRef, useState, useEffect, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../utils';
import {
  Bot,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Brain,
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Scale,
  Info,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
} from 'lucide-react';
import { AnimatedDots } from '../shared';
import { Badge } from '../../atoms/Badge';

// ============================================================================
// Shared Timeline Components
// ============================================================================

interface TimelineIndicatorProps {
  children: ReactNode;
  className?: string;
  isLast?: boolean;
}

const TimelineIndicator = ({ children, className, isLast }: TimelineIndicatorProps) => (
  <div className="flex flex-col items-center">
    <div className={cn(
      'flex items-center justify-center h-8 w-8 rounded-full shrink-0',
      className
    )}>
      {children}
    </div>
    {!isLast && (
      <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 min-h-[16px]" />
    )}
  </div>
);

// ============================================================================
// Agent Activity - Shows agent actions and status
// ============================================================================

/**
 * 'blocked': the mission setup refused the agent's request (agion/Agion#2370).
 * The mission author or an administrator can change the setup.
 */
export type AgentActivityStatus = 'started' | 'running' | 'completed' | 'failed' | 'blocked' | 'waiting';

export interface AgentActivityProps extends HTMLAttributes<HTMLDivElement> {
  agentName: string;
  status: AgentActivityStatus;
  activity?: string;
  icon?: ReactNode;
  duration?: number;
  result?: string;
  isLast?: boolean;
  /** Show approved shield icon after agent name (shows governance approval) */
  approved?: boolean;
  /** Label of the blocked status, shown after the agent name; defaults to English. */
  blockedLabel?: string;
}

const statusConfig: Record<AgentActivityStatus, {
  icon: typeof Play;
  color: string;
  bgColor: string;
  spin?: boolean;
}> = {
  started: {
    icon: Play,
    color: 'text-sky-500',
    bgColor: 'bg-sky-100 dark:bg-sky-900/50',
  },
  running: {
    icon: Loader2,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/50',
    spin: true,
  },
  completed: {
    icon: CheckCircle2,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/50',
  },
  failed: {
    icon: XCircle,
    color: 'text-rose-500',
    bgColor: 'bg-rose-100 dark:bg-rose-900/50',
  },
  blocked: {
    icon: ShieldAlert,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/50',
  },
  waiting: {
    icon: Clock,
    color: 'text-slate-500',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
  },
};

const resultColor: Partial<Record<AgentActivityStatus, string>> = {
  failed: 'text-rose-600 dark:text-rose-400',
  blocked: 'text-amber-700 dark:text-amber-400',
};

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(0);
  return `${mins}m ${secs}s`;
};

export const AgentActivity = forwardRef<HTMLDivElement, AgentActivityProps>(
  ({ className, agentName, status, activity, icon: customIcon, duration, result, isLast, approved, blockedLabel, ...props }, ref) => {
    const config = statusConfig[status];
    const StatusIcon = config.icon;

    return (
      <div ref={ref} className={cn('flex items-start gap-3 min-w-0', className)} {...props}>
        <TimelineIndicator className={config.bgColor} isLast={isLast}>
          {customIcon || <StatusIcon className={cn('h-4 w-4', config.color, config.spin && 'animate-spin')} />}
        </TimelineIndicator>
        <div className={cn('flex-1 min-w-0 min-h-8 flex flex-col justify-center px-3 -ml-1', !isLast && 'pb-4')}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{agentName}</span>
            {approved && (
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            )}
            {status === 'blocked' && (
              <Badge variant="warning" size="sm">{blockedLabel ?? 'Blocked by setup'}</Badge>
            )}
            {duration !== undefined && (
              <span className="text-sm text-slate-400 dark:text-slate-500 tabular-nums">{formatDuration(duration)}</span>
            )}
          </div>
          {activity && <p className="text-sm text-slate-600 dark:text-slate-400">{activity}</p>}
          {result && (
            <p className={cn('text-sm mt-1', resultColor[status] ?? 'text-slate-500 dark:text-slate-400')}>
              {result}
            </p>
          )}
        </div>
      </div>
    );
  }
);
AgentActivity.displayName = 'AgentActivity';

// ============================================================================
// Agent Response - Chat response from agent with governance and feedback
// ============================================================================

export type GovernanceAction = 'allow' | 'deny' | 'obfuscate' | 'escalate' | 'audit';

export interface AgentResponseGovernance {
  action: GovernanceAction;
  reason?: string;
  trustTier?: string;
}

export interface AgentResponseProps extends HTMLAttributes<HTMLDivElement> {
  /** Agent name - can be a string or a custom ReactNode (e.g., a Link component) */
  agentName?: ReactNode;
  children?: ReactNode;
  isLoading?: boolean;
  icon?: ReactNode;
  isLast?: boolean;
  governance?: AgentResponseGovernance;
  showFeedback?: boolean;
  onFeedback?: (type: 'positive' | 'negative') => void;
  /** Show copy button in action row */
  showCopy?: boolean;
  /** Callback when copy button is clicked - if not provided, copies children text content */
  onCopy?: () => void;
  /** Content to copy (if different from rendered children) */
  copyContent?: string;
  /** Show avatar and name header. Set to false for grouped consecutive messages from same sender. */
  showHeader?: boolean;
}

const governanceConfig: Record<GovernanceAction, {
  icon: typeof Shield;
  color: string;
  label: string;
}> = {
  allow: { icon: ShieldCheck, color: 'text-emerald-500', label: 'Allowed' },
  deny: { icon: ShieldAlert, color: 'text-rose-500', label: 'Denied' },
  obfuscate: { icon: AlertTriangle, color: 'text-amber-500', label: 'Masked' },
  escalate: { icon: Scale, color: 'text-sky-500', label: 'Escalated' },
  audit: { icon: Shield, color: 'text-slate-500', label: 'Logged' },
};

export const AgentResponse = forwardRef<HTMLDivElement, AgentResponseProps>(
  ({ className, agentName = 'Assistant', children, isLoading, icon, isLast, governance, showFeedback, onFeedback, showCopy, onCopy, copyContent, showHeader = true, ...props }, ref) => {
    const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);
    const [govExpanded, setGovExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleFeedback = (type: 'positive' | 'negative') => {
      if (feedbackType === type) {
        // Toggle off if clicking the same button
        setFeedbackType(null);
      } else {
        // Set new feedback type
        setFeedbackType(type);
        onFeedback?.(type);
      }
    };

    const handleCopy = async () => {
      if (onCopy) {
        onCopy();
        setCopied(true);
      } else if (copyContent) {
        try {
          await navigator.clipboard.writeText(copyContent);
          setCopied(true);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
      }
      if (copied) return;
      setTimeout(() => setCopied(false), 2000);
    };

    // For 'allow' action, just show shield icon. For others, show full governance indicator.
    const isAllowed = governance?.action === 'allow';
    const showGovBadge = governance && governance.action !== 'allow';
    const GovIcon = showGovBadge ? governanceConfig[governance.action].icon : null;
    const govConfig = showGovBadge ? governanceConfig[governance.action] : null;
    const hasGovDetails = governance?.reason || governance?.trustTier;

    return (
      <div ref={ref} className={cn('flex items-start gap-3 min-w-0', className)} {...props}>
        {showHeader ? (
          <TimelineIndicator className="bg-slate-100 dark:bg-slate-800" isLast={isLast}>
            {icon || <Bot className="h-4 w-4 text-slate-500 dark:text-slate-400" />}
          </TimelineIndicator>
        ) : (
          <div className="w-8 shrink-0" />
        )}
        <div className={cn('flex-1 min-w-0 min-h-8 px-3 -ml-1', !isLast && 'pb-4')}>
          {showHeader && (
          <div className="flex items-center gap-2 min-h-8">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{agentName}</span>
            {/* Approved shield for allowed actions */}
            {isAllowed && (
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            )}
            {/* Governance indicator for non-allow actions */}
            {showGovBadge && GovIcon && govConfig && (
              <button
                type="button"
                onClick={hasGovDetails ? () => setGovExpanded(!govExpanded) : undefined}
                className={cn(
                  'inline-flex items-center gap-1 text-sm',
                  hasGovDetails && 'cursor-pointer hover:opacity-80'
                )}
              >
                <GovIcon className={cn('h-3.5 w-3.5', govConfig.color)} />
                <span className={govConfig.color}>{govConfig.label}</span>
                {hasGovDetails && (
                  <ChevronDown className={cn('h-3 w-3 text-slate-400 transition-transform', govExpanded && 'rotate-180')} />
                )}
              </button>
            )}
          </div>
          )}

          {/* Governance details */}
          {govExpanded && hasGovDetails && (
            <div className="mb-2 px-2.5 py-1.5 rounded text-sm leading-relaxed bg-slate-50 border border-slate-100 text-slate-600 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400">
              {governance?.reason && <p>{governance.reason}</p>}
              {governance?.trustTier && <p className="text-slate-400 dark:text-slate-500 mt-0.5">{governance.trustTier}</p>}
            </div>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center gap-2 py-1">
              <AnimatedDots />
            </div>
          ) : (
            <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed break-words [overflow-wrap:anywhere]">{children}</div>
          )}

          {/* Action row - Feedback and Copy */}
          {(showFeedback || showCopy) && !isLoading && (
            <div className="mt-2">
              <div className={cn(
                'flex items-center gap-0.5 transition-opacity',
                (feedbackType || copied) ? 'opacity-100' : 'opacity-40 hover:opacity-100'
              )}>
                {/* Copy button */}
                {showCopy && (
                  <button
                    type="button"
                    onClick={handleCopy}
                    className={cn(
                      'p-1 rounded transition-colors',
                      copied
                        ? 'text-emerald-500'
                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                    )}
                    aria-label={copied ? 'Copied' : 'Copy message'}
                  >
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </button>
                )}
                {/* Feedback buttons */}
                {showFeedback && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleFeedback('positive')}
                      className={cn(
                        'p-1 rounded transition-colors',
                        feedbackType === 'positive'
                          ? 'text-emerald-500'
                          : 'text-slate-400 hover:text-emerald-500'
                      )}
                      aria-label="Helpful"
                      aria-pressed={feedbackType === 'positive'}
                    >
                      <ThumbsUp className={cn('h-3 w-3', feedbackType === 'positive' && 'fill-current')} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFeedback('negative')}
                      className={cn(
                        'p-1 rounded transition-colors',
                        feedbackType === 'negative'
                          ? 'text-rose-500'
                          : 'text-slate-400 hover:text-rose-500'
                      )}
                      aria-label="Not helpful"
                      aria-pressed={feedbackType === 'negative'}
                    >
                      <ThumbsDown className={cn('h-3 w-3', feedbackType === 'negative' && 'fill-current')} />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
AgentResponse.displayName = 'AgentResponse';

// ============================================================================
// User Message - Human user input with emphasis
// ============================================================================

export interface UserMessageProps extends HTMLAttributes<HTMLDivElement> {
  userName?: string;
  children: ReactNode;
  avatar?: ReactNode;
  initials?: string;
  isLast?: boolean;
  /** Whether this is the current user's own message (default: true for "You") */
  isCurrentUser?: boolean;
  /** Show avatar and name header. Set to false for grouped consecutive messages from same sender. */
  showHeader?: boolean;
}

export const UserMessage = forwardRef<HTMLDivElement, UserMessageProps>(
  ({ className, userName = 'You', children, avatar, initials, isLast, isCurrentUser, showHeader = true, ...props }, ref) => {
    const displayInitials = initials || userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    // Default to true if userName is 'You', otherwise false
    const isSelf = isCurrentUser ?? userName === 'You';

    // Wrap avatar with colored background circle for visibility
    const avatarWithBackground = avatar ? (
      <div className="relative w-5 h-5 flex items-center justify-center">
        {avatar}
      </div>
    ) : (
      <span className={cn(
        'text-xs font-semibold',
        isSelf
          ? 'text-sky-700 dark:text-sky-300'
          : 'text-indigo-700 dark:text-indigo-300'
      )}>
        {displayInitials}
      </span>
    );

    return (
      <div ref={ref} className={cn('flex items-start gap-3 min-w-0', className)} {...props}>
        {showHeader ? (
          <TimelineIndicator
            className={isSelf
              ? 'bg-sky-200 dark:bg-sky-800'
              : 'bg-indigo-200 dark:bg-indigo-800'
            }
            isLast={isLast}
          >
            {avatarWithBackground}
          </TimelineIndicator>
        ) : (
          <div className="w-8 shrink-0" />
        )}
        <div className={cn('flex-1 min-w-0 min-h-8', !isLast && 'pb-4')}>
          {/* Visible background emphasis - different colors for self vs others */}
          <div className={cn(
            'rounded-lg px-3 py-2 -ml-1 border',
            isSelf
              ? 'bg-sky-50 border-sky-200 dark:bg-sky-950/60 dark:border-sky-800'
              : 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950/60 dark:border-indigo-800'
          )}>
            {showHeader && (
            <div className="flex items-center gap-2 min-h-5">
              <span className={cn(
                'text-sm font-medium',
                isSelf
                  ? 'text-sky-900 dark:text-sky-100'
                  : 'text-indigo-900 dark:text-indigo-100'
              )}>{userName}</span>
            </div>
            )}
            <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed break-words [overflow-wrap:anywhere]">{children}</div>
          </div>
        </div>
      </div>
    );
  }
);
UserMessage.displayName = 'UserMessage';

// ============================================================================
// System Message - System notifications and announcements
// ============================================================================

export interface SystemMessageProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: 'info' | 'warning' | 'success' | 'error';
  isLast?: boolean;
}

const systemVariants = {
  info: { icon: Info, bgColor: 'bg-slate-100 dark:bg-slate-800', iconColor: 'text-slate-500' },
  warning: { icon: AlertTriangle, bgColor: 'bg-amber-100 dark:bg-amber-900/50', iconColor: 'text-amber-500' },
  success: { icon: CheckCircle2, bgColor: 'bg-emerald-100 dark:bg-emerald-900/50', iconColor: 'text-emerald-500' },
  error: { icon: XCircle, bgColor: 'bg-rose-100 dark:bg-rose-900/50', iconColor: 'text-rose-500' },
};

export const SystemMessage = forwardRef<HTMLDivElement, SystemMessageProps>(
  ({ className, children, variant = 'info', isLast, ...props }, ref) => {
    const config = systemVariants[variant];
    const Icon = config.icon;

    return (
      <div ref={ref} className={cn('flex items-start gap-3 min-w-0', className)} {...props}>
        <TimelineIndicator className={config.bgColor} isLast={isLast}>
          <Icon className={cn('h-4 w-4', config.iconColor)} />
        </TimelineIndicator>
        <div className={cn('flex-1 min-w-0 min-h-8 flex items-center px-3 -ml-1', !isLast && 'pb-4')}>
          <div className="text-sm text-slate-500 dark:text-slate-400 italic">{children}</div>
        </div>
      </div>
    );
  }
);
SystemMessage.displayName = 'SystemMessage';

// ============================================================================
// Thinking Block - Shows agent reasoning process
// ============================================================================

export type ThinkingStepStatus = 'pending' | 'active' | 'complete' | 'error';

export interface ThinkingStep {
  id: string;
  label: string;
  status: ThinkingStepStatus;
}

export interface ThinkingBlockProps extends HTMLAttributes<HTMLDivElement> {
  agentName?: string;
  currentStep?: string;
  steps?: ThinkingStep[];
  isComplete?: boolean;
  expandable?: boolean;
  startTime?: Date;
  isLast?: boolean;
}

export const ThinkingBlock = forwardRef<HTMLDivElement, ThinkingBlockProps>(
  ({ className, agentName = 'Agent', currentStep, steps = [], isComplete = false, expandable = true, startTime, isLast, ...props }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
      if (!startTime || isComplete) return;
      const interval = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime.getTime()) / 100) / 10);
      }, 100);
      return () => clearInterval(interval);
    }, [startTime, isComplete]);

    const formatElapsed = (seconds: number) => {
      if (seconds < 60) return `${seconds.toFixed(1)}s`;
      const mins = Math.floor(seconds / 60);
      const secs = (seconds % 60).toFixed(0);
      return `${mins}:${secs.padStart(2, '0')}`;
    };

    const getStepIcon = (status: ThinkingStepStatus) => {
      switch (status) {
        case 'complete': return <CheckCircle2 className="h-3 w-3 text-emerald-500" />;
        case 'active': return <Loader2 className="h-3 w-3 text-purple-500 animate-spin" />;
        case 'error': return <XCircle className="h-3 w-3 text-rose-500" />;
        default: return <div className="h-3 w-3 rounded-full border border-slate-300 dark:border-slate-600" />;
      }
    };

    return (
      <div ref={ref} className={cn('flex items-start gap-3 min-w-0', className)} {...props}>
        <TimelineIndicator className="bg-purple-100 dark:bg-purple-900/50" isLast={isLast}>
          {isComplete ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <Brain className="h-4 w-4 text-purple-500 animate-pulse" />
          )}
        </TimelineIndicator>
        <div className={cn('flex-1 min-w-0 min-h-8 px-3 -ml-1', !isLast && 'pb-4')}>
          <div className="flex items-center gap-2 min-h-8">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{agentName}</span>
            {startTime && !isComplete && (
              <span className="text-sm text-slate-400 dark:text-slate-500 tabular-nums">{formatElapsed(elapsed)}</span>
            )}
            {expandable && steps.length > 0 && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-auto p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
              </button>
            )}
          </div>
          {!isComplete && (
            <div className="flex items-center gap-2 mt-1">
              <AnimatedDots size="sm" variant="brand" />
              <span className="text-sm text-slate-600 dark:text-slate-400">{currentStep || 'Thinking...'}</span>
            </div>
          )}
          {isExpanded && steps.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {steps.map((step) => (
                <div key={step.id} className="flex items-center gap-2">
                  {getStepIcon(step.status)}
                  <span className={cn(
                    'text-sm',
                    step.status === 'complete' && 'text-slate-600 dark:text-slate-400',
                    step.status === 'active' && 'text-purple-600 dark:text-purple-400',
                    step.status === 'error' && 'text-rose-600 dark:text-rose-400',
                    step.status === 'pending' && 'text-slate-400 dark:text-slate-500'
                  )}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);
ThinkingBlock.displayName = 'ThinkingBlock';
